/**
 * ServiceWorkerManager - Manages service worker registration and updates
 *
 * This class provides centralized control over service worker lifecycle,
 * including registration, update detection, and a kill-switch for debugging.
 */

export interface ServiceWorkerStatus {
  isSupported: boolean;
  isRegistered: boolean;
  hasUpdate: boolean;
  activeWorker: ServiceWorker | null;
}

export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private readonly logPrefix = '[SW Manager]';

  /**
   * Check if service workers are supported in this browser
   */
  private isServiceWorkerSupported(): boolean {
    return 'serviceWorker' in navigator;
  }

  /**
   * Register the service worker and set up update listeners
   * @param scriptUrl - URL to the service worker script (default: '/service-worker.js')
   */
  async register(scriptUrl: string = '/service-worker.js'): Promise<void> {
    if (!this.isServiceWorkerSupported()) {
      console.warn(`${this.logPrefix} Service workers are not supported in this browser`);
      return;
    }

    try {
      this.registration = await navigator.serviceWorker.register(scriptUrl, {
        type: 'module',
        updateViaCache: 'none', // Always check for updates
      });

      // Set up update listener
      this.setupUpdateListener();
      // Update checks happen automatically on navigation events
    } catch (error) {
      console.error(`${this.logPrefix} Registration failed:`, error);
      throw error;
    }
  }

  /**
   * Set up listener for service worker updates
   */
  private setupUpdateListener(): void {
    if (!this.registration) return;

    // Listen for updates to the service worker
    this.registration.addEventListener('updatefound', () => {
      const installingWorker = this.registration?.installing;

      if (!installingWorker) return;

      installingWorker.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New service worker is available
            // Automatically skip waiting and activate the new service worker
            installingWorker.postMessage({ type: 'SKIP_WAITING' });
          } else {
            // First time installation — no action needed
          }
        } else if (installingWorker.state === 'activated') {
          // Service worker activated
        }
      });
    });

    // Log controller changes but do NOT reload the page.
    // The new service worker takes control via clients.claim() in service-worker.ts.
    // Automatic reload causes infinite loops when the browser detects the SW as
    // "new" on every page load (e.g., due to updateViaCache: 'none' + CDN behavior).
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Controller changed, new service worker is now active
    });
  }

  /**
   * Manually check for service worker updates
   */
  async checkForUpdates(): Promise<void> {
    if (!this.registration) {
      console.warn(`${this.logPrefix} Cannot check for updates: No registration found`);
      return;
    }

    // Don't check if we're in the middle of an update
    if (this.registration.installing) {
      return;
    }

    try {
      await this.registration.update();
    } catch (error) {
      console.error(`${this.logPrefix} Update check failed:`, error);
    }
  }

  /**
   * Unregister all service workers (kill-switch for debugging)
   */
  async unregisterAll(): Promise<void> {
    if (!this.isServiceWorkerSupported()) {
      console.warn(`${this.logPrefix} Service workers are not supported in this browser`);
      return;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      if (registrations.length === 0) {
        return;
      }

      await Promise.all(
        registrations.map(async (registration) => {
          const unregistered = await registration.unregister();
          return unregistered;
        })
      );

      this.registration = null;
    } catch (error) {
      console.error(`${this.logPrefix} Failed to unregister service workers:`, error);
      throw error;
    }
  }

  /**
   * Get current registration status
   */
  getRegistrationStatus(): ServiceWorkerStatus {
    if (!this.isServiceWorkerSupported()) {
      return {
        isSupported: false,
        isRegistered: false,
        hasUpdate: false,
        activeWorker: null,
      };
    }

    return {
      isSupported: true,
      isRegistered: !!this.registration,
      hasUpdate: !!this.registration?.waiting,
      activeWorker: this.registration?.active || navigator.serviceWorker.controller,
    };
  }
}

/**
 * Singleton instance for global access
 */
export const serviceWorkerManager = new ServiceWorkerManager();
